import { loadStripe, type Stripe } from '@stripe/stripe-js'

import { REGION_IDS, type RegionId } from '@snootsvet/shared'

/**
 * Publishable key per region, mirroring the backend's `STRIPE_SECRET_KEY` /
 * `STRIPE_US_SECRET_KEY` split. UK is the always-configured default; US is
 * optional, so environments without a US Stripe account still boot.
 */
const PUBLISHABLE_KEYS: Record<RegionId, string | undefined> = {
  [REGION_IDS.UK]: import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY,
  [REGION_IDS.US]: import.meta.env.VITE_STRIPE_US_PUBLISHABLE_KEY,
}

/** The env var holding each region's publishable key, for the not-configured message. */
export const PUBLISHABLE_KEY_VARS: Record<RegionId, string> = {
  [REGION_IDS.UK]: 'VITE_STRIPE_PUBLISHABLE_KEY',
  [REGION_IDS.US]: 'VITE_STRIPE_US_PUBLISHABLE_KEY',
}

/**
 * Loaded Stripe.js clients, one per region.
 *
 * This cache is load-bearing, not an optimisation. `stripeForRegion` is called
 * *during render* (see `AddPaymentMethodForm`), and each `loadStripe` call builds a
 * fresh `Stripe` instance even though the script itself is only fetched once. Since
 * `<Elements stripe={...}>` doesn't pick up a changed `stripe` prop — it warns and
 * keeps the old instance — an uncached call would hand `<Elements>` a new instance on
 * every render and strand it on a stale one. The cache is what makes calling this
 * from render safe.
 */
const clients = new Map<RegionId, Promise<Stripe | null>>()

/**
 * Returns the Stripe.js instance for a region, or `null` when that region has no
 * publishable key configured.
 *
 * Each region is a separate Stripe account, so a client secret can only be
 * confirmed by the instance built from *its* account's key. Callers must pass the
 * region the secret was minted in (see `PostMemberSetupIntentResponse.region`),
 * never the viewer's active region.
 *
 * Named to match the backend's `stripeForRegion` — same question, same answer,
 * same spelling on both sides of the wire.
 */
export function stripeForRegion(region: RegionId): Promise<Stripe | null> | null {
  const key = PUBLISHABLE_KEYS[region]
  if (!key) return null

  const cached = clients.get(region)
  if (cached) return cached

  const client = loadStripe(key)
  clients.set(region, client)
  return client
}

/** Stripe object-id prefixes, keyed by the entity type staff enter IDs for. */
const STRIPE_ID_PREFIX = {
  customer: 'cus_',
  subscription: 'sub_',
} as const

export type StripeIdEntity = keyof typeof STRIPE_ID_PREFIX

export type StripeIdValidationResult = 'valid' | 'wrong_entity' | 'invalid_format'

/**
 * Validates a hand-entered Stripe ID against the entity its field expects.
 * Stripe object IDs are prefixed by type (cus_… / sub_…), so we can catch the
 * common mistake of pasting a customer ID into a subscription field, or vice versa.
 */
export function validateStripeId(id: string, entity: StripeIdEntity): StripeIdValidationResult {
  const trimmed = id.trim()
  if (trimmed === '') return 'valid'

  if (trimmed.startsWith(STRIPE_ID_PREFIX[entity])) return 'valid'

  const other = entity === 'customer' ? 'subscription' : 'customer'
  if (trimmed.startsWith(STRIPE_ID_PREFIX[other])) return 'wrong_entity'

  return 'invalid_format'
}
